// What the Dock is talking to. Screens publish context (the selected card, the current sweep, a
// focused document); the Dock walks a scope ladder card → this sweep → feed → configuration with
// ↑ (broader) and ↓ (narrower). Pure ladder helpers are exported for tests.
// Scope ladder adapted from EveryInc/tend src/shell/Dock.tsx (MIT, Copyright (c) 2026 Every, Inc.).
import { useSyncExternalStore } from "react";
import type { ScopeTarget } from "@shared/core";

export type DockScope = "card" | "sweep" | "feed" | "configuration";

/** Narrow → broad. */
export const SCOPE_ORDER: readonly DockScope[] = ["card", "sweep", "feed", "configuration"];

export interface DockContext {
  feedId: string | null;
  feedName: string | null;
  card: { id: string; title: string } | null;
  batchId: string | null;
  docKey: string | null;
  docTitle: string | null;
}

export function scopeLadder(ctx: Pick<DockContext, "card">): DockScope[] {
  return SCOPE_ORDER.filter((s) => s !== "card" || ctx.card !== null);
}

/** One step broader or narrower, clamped to the ladder. */
export function zoomScope(
  ladder: readonly DockScope[],
  current: DockScope,
  direction: "broader" | "narrower",
): DockScope {
  const index = Math.max(0, ladder.indexOf(current));
  const next = direction === "broader" ? index + 1 : index - 1;
  return ladder[Math.min(ladder.length - 1, Math.max(0, next))] ?? current;
}

/** The scope actually used: a card scope without a card falls back to the sweep. */
export function effectiveScope(scope: DockScope, ctx: Pick<DockContext, "card">): DockScope {
  return scope === "card" && !ctx.card ? "sweep" : scope;
}

export function defaultDocKey(feedId: string): string {
  return `feeds/${feedId}/policy.md`;
}

export function scopeTarget(scope: DockScope, ctx: DockContext): ScopeTarget | null {
  switch (effectiveScope(scope, ctx)) {
    case "card":
      return ctx.card ? { kind: "card", cardId: ctx.card.id } : null;
    case "sweep":
      return ctx.batchId ? { kind: "sweep", batchId: ctx.batchId } : { kind: "sweep" };
    case "feed":
      return { kind: "feed" };
    case "configuration": {
      const docKey = ctx.docKey ?? (ctx.feedId ? defaultDocKey(ctx.feedId) : null);
      return docKey ? { kind: "configuration", docKey } : null;
    }
  }
}

export function scopeLabel(scope: DockScope, ctx: DockContext): string {
  switch (effectiveScope(scope, ctx)) {
    case "card":
      return ctx.card?.title ?? "This card";
    case "sweep":
      return "This round";
    case "feed":
      return ctx.feedName ?? "This feed";
    case "configuration":
      return ctx.docTitle ?? "Feed policy";
  }
}

/** What happens after Enter, for the confirmation toast. */
export function scopeOutcome(scope: DockScope, ctx: DockContext): string {
  switch (effectiveScope(scope, ctx)) {
    case "card":
      return "Handed to Claude for this card";
    case "sweep":
      return "Claude will re-sort this round with your note";
    case "feed":
      return "Handed to Claude";
    case "configuration":
      return "Claude will propose a change. You'll review it in Learning";
  }
}

// ---------------------------------------------------------------------------------------------
// Store

interface DockState extends DockContext {
  scope: DockScope;
  /** Bumped to ask the Dock to focus its input (`.`). */
  focusSignal: number;
}

let state: DockState = {
  feedId: null,
  feedName: null,
  card: null,
  batchId: null,
  docKey: null,
  docTitle: null,
  scope: "sweep",
  focusSignal: 0,
};
const listeners = new Set<() => void>();

function set(patch: Partial<DockState>) {
  const next = { ...state, ...patch };
  if (
    Object.keys(patch).every((k) =>
      Object.is(next[k as keyof DockState], state[k as keyof DockState]),
    )
  ) {
    return;
  }
  state = next;
  for (const l of listeners) l();
}

export const dock = {
  get: () => state,
  setFeed: (feedId: string | null, feedName: string | null, batchId: string | null) =>
    set({ feedId, feedName, batchId }),
  /** The card the reader is looking at; the card scope follows it. */
  setCard: (card: { id: string; title: string } | null) => {
    const same = state.card?.id === card?.id && state.card?.title === card?.title;
    if (same) return;
    set({ card });
  },
  setScope: (scope: DockScope) => set({ scope }),
  setDoc: (docKey: string | null, docTitle: string | null) => set({ docKey, docTitle }),
  /** A screen's preferred scope on arrival (e.g. configuration on How Claude works). */
  prefer: (scope: DockScope) => set({ scope }),
  focus: () => set({ focusSignal: state.focusSignal + 1 }),
};

export function useDock(): DockState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}
